import { createSlice } from '@reduxjs/toolkit';
import driverService from '../services/driverService';
import { DriverLocation } from './models/driver-location.model';
import { Driver } from './models/driver.model';

type DriverLocationsById = { [driverID: number]: DriverLocation };

type DriverLocationsByTripLegReferenceId = {
  [tripLegReferenceID: string]: DriverLocation;
};

type DriversByProvider = { [providerId: number]: Driver[] };

interface DriverState {
  driverLocation: DriverLocation | null;
  loadingDriverLocation: boolean;
  loadedDriverLocation: boolean;
  driverLocationsById: DriverLocationsById;
  driverLocationsByTripLegReferenceId: DriverLocationsByTripLegReferenceId;
  driversByProvider: DriversByProvider;
  loadingDriversByProvider: boolean;
  loadedDriversByProvider: boolean;
}

const initialState: DriverState = {
  driverLocation: null,
  loadingDriverLocation: false,
  loadedDriverLocation: false,
  driverLocationsById: {},
  driverLocationsByTripLegReferenceId: {},
  driversByProvider: {},
  loadingDriversByProvider: false,
  loadedDriversByProvider: false,
};

export const slice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    setDriverLocation: (
      state: DriverState,
      action: { type: string; payload: any }, // TODO: this needs to change
    ) => {
      state.driverLocation = action.payload;
    },
    setDriverLocationForDriverId: (
      state: DriverState,
      action: {
        type: string;
        payload: { driverID: number; location: DriverLocation };
      },
    ) => {
      const { driverID, location } = action.payload;
      state.driverLocationsById[driverID] = location;
    },
    setDriverLocationForTripLegReferenceId: (
      state: DriverState,
      action: {
        type: string;
        payload: { tripLegReferenceID: string; location: DriverLocation };
      },
    ) => {
      const { tripLegReferenceID, location } = action.payload;
      state.driverLocationsByTripLegReferenceId[tripLegReferenceID] = location;
    },
    setLoadingDriverLocation: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingDriverLocation = action.payload;
    },
    setLoadedDriverLocation: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedDriverLocation = action.payload;
    },
    clearDriverLocation: (state: DriverState) => {
      state.driverLocation = null;
    },
    setDriversByProvider: (
      state: DriverState,
      action: {
        type: string;
        payload: { providerId: number; drivers: Driver[] };
      },
    ) => {
      const { providerId, drivers } = action.payload;
      state.driversByProvider[providerId] = drivers;
    },
    setLoadingDriversByProvider: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingDriversByProvider = action.payload;
    },
    setLoadedDriversByProvider: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedDriversByProvider = action.payload;
    },
  },
});

export const { setDriverLocation } = slice.actions;
export const { setDriverLocationForDriverId } = slice.actions;
export const { setDriverLocationForTripLegReferenceId } = slice.actions;
export const { setLoadingDriverLocation } = slice.actions;
export const { setLoadedDriverLocation } = slice.actions;
export const { clearDriverLocation } = slice.actions;
export const { setDriversByProvider } = slice.actions;
export const { setLoadingDriversByProvider } = slice.actions;
export const { setLoadedDriversByProvider } = slice.actions;

export const fetchDriverLocationAsync =
  (driverID: number) => (dispatch: Function) => {
    dispatch(setLoadingDriverLocation(true));
    dispatch(setLoadedDriverLocation(false));
    driverService.getDriverLocation(driverID).subscribe({
      next: (location) => {
        dispatch(setDriverLocation(location));
        dispatch(setDriverLocationForDriverId({ driverID, location }));
        dispatch(setLoadingDriverLocation(false));
        dispatch(setLoadedDriverLocation(true));
      },
      error: (err) => {
        dispatch(setLoadingDriverLocation(false));
      },
    });
  };

export const fetchDriversByProviderAsync =
  (providerId: number) => (dispatch: Function) => {
    dispatch(setLoadingDriversByProvider(true));
    dispatch(setLoadedDriversByProvider(false));
    driverService.getDriversByProvider(providerId).subscribe({
      next: (pagedList) => {
        dispatch(
          setDriversByProvider({ providerId, drivers: pagedList.items }),
        );
        dispatch(setLoadingDriversByProvider(false));
        dispatch(setLoadedDriversByProvider(true));
      },
      error: (err) => {
        dispatch(setLoadingDriversByProvider(false));
      },
    });
  };
export const selectDriverLocation = (state: { driver: DriverState }) =>
  state.driver.driverLocation;
export const selectDriverLocationsById = (state: { driver: DriverState }) =>
  state.driver.driverLocationsById;
export const selectDriverLocationsByTripLegReferenceId = (state: {
  driver: DriverState;
}) => state.driver.driverLocationsByTripLegReferenceId;
export const selectLoadingDriverLocation = (state: { driver: DriverState }) =>
  state.driver.loadingDriverLocation;
export const selectLoadedDriverLocation = (state: { driver: DriverState }) =>
  state.driver.loadedDriverLocation;
export const selectDriversByProvider = (
  state: { driver: DriverState },
  providerId: number,
) => state.driver.driversByProvider[providerId] || [];
export const selectLoadingDriversByProvider = (state: {
  driver: DriverState;
}) => state.driver.loadingDriversByProvider;
export const selectLoadedDriversByProvider = (state: { driver: DriverState }) =>
  state.driver.loadedDriversByProvider;

export default slice.reducer;
